import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { User, Plus, X, Save, Download, XCircle, CreditCard, AlertTriangle, Shield, FileText } from 'lucide-react';
import { downloadUserDataPdf } from '../utils/userDataPdf';

import API from '../utils/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Profile = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    address: '',
    children: []
  });
  const [newChild, setNewChild] = useState({ name: '', dob: '' });
  const [usernameError, setUsernameError] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        children: user.children || []
      });
    }
  }, [user]);

  const handleAddChild = () => {
    if (newChild.name && newChild.dob) {
      setProfileData({
        ...profileData,
        children: [...profileData.children, { ...newChild }]
      });
      setNewChild({ name: '', dob: '' });
    }
  };

  const handleRemoveChild = async (index) => {
    if (!window.confirm('Remove this child from your profile?')) return;
    const updated = profileData.children.filter((_, i) => i !== index);
    const newData = { ...profileData, children: updated };
    setProfileData(newData);
    try {
      await axios.patch(`${API}/profile`, { children: updated }, { withCredentials: true });
      await checkAuth();
    } catch (error) {
      console.error('Error removing child:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setUsernameError('');
    try {
      await axios.patch(`${API}/profile`, profileData, { withCredentials: true });
      await checkAuth();
      alert('Profile updated successfully!');
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setUsernameError('Username already taken');
      } else if (error.response && error.response.data && error.response.data.detail) {
        setUsernameError(error.response.data.detail);
      } else {
        alert('Failed to save profile');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card-surface p-6">
        <div className="flex items-center gap-3 mb-2">
          <User className="w-6 h-6 text-[#2D2560]" />
          <h2 className="text-2xl font-bold">Profile</h2>
        </div>
        <p className="text-white/60">Manage your personal information and children</p>
      </div>

      <div className="card-surface p-6 space-y-6">
        <h3 className="text-lg font-bold">Personal Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Full Name</label>
            <input
              type="text"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={profileData.username}
              onChange={(e) => { setProfileData({ ...profileData, username: e.target.value }); setUsernameError(''); }}
              placeholder="Choose a unique username"
              className={'w-full bg-white/5 border rounded-lg px-4 py-2 ' + (usernameError ? 'border-[#EF4444] text-[#EF4444]' : 'border-white/10 text-white')}
              data-testid="username-input"
            />
            {usernameError && <p className="text-[#EF4444] text-xs mt-1">{usernameError}</p>}
            <p className="text-white/30 text-xs mt-1">3-20 characters. Letters, numbers, underscores, and periods only. This will appear on the leaderboard and header.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={profileData.email}
              disabled
              className="w-full bg-white/5 border border-white/10 text-white/60 rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <input
              type="tel"
              value={profileData.phone}
              onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Address</label>
            <input
              type="text"
              value={profileData.address}
              onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
            />
          </div>
        </div>
      </div>

      <div className="card-surface p-6 space-y-6">
        <h3 className="text-lg font-bold">Children</h3>
        
        <div className="space-y-3">
          {profileData.children.map((child, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
              <div className="flex-1">
                <p className="font-semibold">{child.name}</p>
                <p className="text-sm text-white/60">DOB: {new Date(child.dob).toLocaleDateString()}</p>
              </div>
              <button
                onClick={() => handleRemoveChild(index)}
                className="p-2 text-white/30 hover:text-white/60 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-4">
          <p className="text-sm font-medium mb-3">Add Child</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={newChild.name}
              onChange={(e) => setNewChild({ ...newChild, name: e.target.value })}
              placeholder="Child's name"
              className="flex-1 bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
            />
            <input
              type="date"
              value={newChild.dob}
              onChange={(e) => setNewChild({ ...newChild, dob: e.target.value })}
              className="bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
            />
            <button
              onClick={handleAddChild}
              disabled={!newChild.name || !newChild.dob}
              className="px-4 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 flex-shrink-0 font-semibold"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="w-full bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 font-semibold rounded-lg py-3 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Save className="w-5 h-5" />
        {loading ? 'Saving...' : 'Save Profile'}
      </button>

      {/* Account Management */}
      <AccountManagement user={user} checkAuth={checkAuth} />
    </div>
  );
};

function AccountManagement({ user, checkAuth }) {
  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deletionPending, setDeletionPending] = useState(false);
  const [deletionDate, setDeletionDate] = useState(null);

  // Business Record Affidavit form
  const [braOpen, setBraOpen] = useState(false);
  const [braDelivery, setBraDelivery] = useState('email');
  const splitName = (user && user.name ? user.name.split(/\s+/) : []);
  const [braFirstName, setBraFirstName] = useState(splitName[0] || '');
  const [braLastName, setBraLastName] = useState(splitName.slice(1).join(' ') || '');
  const [braMailingAddress, setBraMailingAddress] = useState('');
  const [braEligible, setBraEligible] = useState([]); // [{ kind, kind_label, source_id, reference_number, label }]
  const [braSelectedRefs, setBraSelectedRefs] = useState({}); // ref# -> true
  const [braLoadingRecords, setBraLoadingRecords] = useState(false);
  const [braSubmitting, setBraSubmitting] = useState(false);
  const [braReceipt, setBraReceipt] = useState(null); // { request_id }
  const BRA_PRICES = { email: 35, mail: 40, certified_mail: 50 };
  const todayStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const requiresMailing = braDelivery === 'mail' || braDelivery === 'certified_mail';

  useEffect(function () {
    axios.get(`${API}/legal/account/deletion-status`, { withCredentials: true })
      .then(function (r) {
        setDeletionPending(r.data.deletion_pending || false);
        setDeletionDate(r.data.deletion_scheduled_at || null);
      })
      .catch(function () {});
  }, []);

  // Verify Stripe payment when redirected back (?bra_session=cs_...)
  useEffect(function () {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('bra_session');
    const cancelled = params.get('bra') === 'cancelled';
    if (cancelled) {
      params.delete('bra');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
      alert('Payment cancelled. Your Business Record Affidavit request was not submitted.');
      return;
    }
    if (!sid) return;
    axios.get(`${API}/legal/business-record-affidavit/verify/${sid}`, { withCredentials: true })
      .then(function (r) {
        if (r.data && r.data.payment_status === 'paid' && r.data.request_id) {
          setBraReceipt({ request_id: r.data.request_id });
        } else {
          alert('Payment is still processing. Please refresh in a moment.');
        }
      })
      .catch(function () { alert('Could not verify payment.'); })
      .finally(function () {
        params.delete('bra_session');
        window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the user's certifiable records the moment the BRA form opens.
  useEffect(function () {
    if (!braOpen || braEligible.length > 0) return;
    setBraLoadingRecords(true);
    axios.get(`${API}/legal/business-record-affidavit/eligible-records`, { withCredentials: true })
      .then(function (r) { setBraEligible((r.data && r.data.items) || []); })
      .catch(function () {})
      .finally(function () { setBraLoadingRecords(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [braOpen]);

  function toggleBraRecord(refNum) {
    setBraSelectedRefs(function (prev) {
      var next = Object.assign({}, prev);
      if (next[refNum]) delete next[refNum]; else next[refNum] = true;
      return next;
    });
  }

  function submitBra() {
    if (!braFirstName.trim() || !braLastName.trim()) {
      alert('Please enter both first and last name.');
      return;
    }
    if (requiresMailing && braMailingAddress.trim().length < 10) {
      alert('Please enter a complete mailing address (street, city, state, ZIP).');
      return;
    }
    var selected = braEligible.filter(function (r) { return braSelectedRefs[r.reference_number]; });
    if (selected.length === 0) {
      alert('Please select at least one record to certify.');
      return;
    }
    setBraSubmitting(true);
    axios.post(`${API}/legal/business-record-affidavit/checkout`, {
      delivery_method: braDelivery,
      first_name: braFirstName.trim(),
      last_name: braLastName.trim(),
      mailing_address: requiresMailing ? braMailingAddress.trim() : '',
      selected_records: selected,
      origin_url: window.location.origin,
    }, { withCredentials: true })
      .then(function (r) {
        if (r.data && r.data.url) {
          window.location.href = r.data.url; // Stripe Checkout
        } else {
          alert('Payment session failed.');
          setBraSubmitting(false);
        }
      })
      .catch(function (e) {
        const msg = (e && e.response && e.response.data && e.response.data.detail) || 'Could not start payment.';
        alert(msg);
        setBraSubmitting(false);
      });
  }

  function handleExport() {
    setExporting(true);
    axios.post(`${API}/account/export`, {}, { withCredentials: true })
      .then(function (r) {
        const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'parentum-data-export.json';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(function () { alert('Export failed'); })
      .finally(function () { setExporting(false); });
  }

  function handleExportPdf() {
    setExporting(true);
    axios.post(`${API}/account/export`, {}, { withCredentials: true })
      .then(function (r) { return downloadUserDataPdf(r.data, r.data?.account?.name || r.data?.account?.email); })
      .catch(function () { alert('PDF export failed'); })
      .finally(function () { setExporting(false); });
  }

  function handleCancelSub() {
    if (!window.confirm('Cancel your subscription? You will be downgraded to the free plan. Your data will be preserved.')) return;
    setCancelling(true);
    axios.post(`${API}/account/cancel-subscription`, {}, { withCredentials: true })
      .then(function () {
        alert('Subscription cancelled. You are now on the free plan.');
        checkAuth();
      })
      .catch(function (e) { alert(e.response ? e.response.data.detail : 'Failed'); })
      .finally(function () { setCancelling(false); });
  }

  function handleDeleteAccount() {
    if (!window.confirm('Delete your account? You will be logged out immediately. Your data will be retained for 90 days for administrative review and then permanently purged. This cannot be undone.')) return;
    setDeleting(true);
    axios.post(`${API}/account/delete`, { password: deletePassword }, { withCredentials: true })
      .then(function () {
        alert('Your account has been deleted. Your data will be retained for 90 days, then permanently purged.');
        setShowDelete(false);
        setDeletePassword('');
        // Force a hard redirect to login since the session is now invalid.
        window.location.href = '/login';
      })
      .catch(function (e) { alert(e.response ? e.response.data.detail : 'Request failed'); })
      .finally(function () { setDeleting(false); });
  }

  function handleCancelDeletion() {
    axios.post(`${API}/legal/account/cancel-deletion`, {}, { withCredentials: true })
      .then(function () {
        setDeletionPending(false);
        setDeletionDate(null);
        alert('Deletion cancelled. Your account is safe.');
      })
      .catch(function (e) { alert(e.response ? e.response.data.detail : 'Failed'); });
  }

  return (
    <div className="space-y-4 pt-2" data-testid="account-management">
      <div className="card-surface p-6 space-y-4">
        <h3 className="text-lg font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-white/40" /> Account & Privacy</h3>

        {/* Export */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl">
          <div>
            <p className="text-sm font-medium text-white/70">Download My Data</p>
            <p className="text-xs text-white/35 mt-0.5">Export every record Parentum holds for your account.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button onClick={handleExportPdf} disabled={exporting}
              className="px-4 py-2 bg-[#2D2560] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-30 flex items-center justify-center gap-2 transition-opacity"
              data-testid="export-data-pdf-btn">
              <FileText className="w-4 h-4" />
              {exporting ? 'Building…' : 'Download PDF'}
            </button>
            <button onClick={handleExport} disabled={exporting}
              className="px-4 py-2 bg-[#5A4E9E] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-30 flex items-center justify-center gap-2 transition-opacity"
              data-testid="export-data-btn">
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'JSON'}
            </button>
          </div>
        </div>

        {/* Cancel Subscription */}
        {user && user.subscription_plan && user.subscription_plan !== 'free' && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl">
            <div>
              <p className="text-sm font-medium text-white/70">Cancel Subscription</p>
              <p className="text-xs text-white/35 mt-0.5">Downgrade to free plan. Your data is preserved.</p>
            </div>
            <button onClick={handleCancelSub} disabled={cancelling}
              className="px-4 py-2 bg-[#EAB308] text-black text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-30 flex items-center justify-center gap-2 transition-opacity flex-shrink-0"
              data-testid="cancel-sub-btn">
              <CreditCard className="w-4 h-4" />
              {cancelling ? 'Cancelling...' : 'Cancel Plan'}
            </button>
          </div>
        )}

        {/* Business Record Affidavit */}
        <div className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl space-y-3" data-testid="bra-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-white/70 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#5A4E9E]" /> Business Record Affidavit
              </p>
              <p className="text-xs text-white/35 mt-0.5">Request a notarized affidavit of your Parentum records for court. $35 email · $40 mail · $50 certified mail.</p>
            </div>
            {!braReceipt && (
              <button onClick={function () { setBraOpen(!braOpen); }}
                className="px-4 py-2 border border-[#5A4E9E]/30 text-[#5A4E9E] text-sm font-medium rounded-lg hover:bg-[#5A4E9E]/10 flex items-center justify-center gap-2 transition-colors flex-shrink-0"
                data-testid="bra-toggle-btn">
                <FileText className="w-4 h-4" /> {braOpen ? 'Cancel' : 'Request Affidavit'}
              </button>
            )}
          </div>

          {braReceipt && (
            <div className="p-3 bg-[#10B981]/10 border border-[#10B981]/25 rounded-lg" data-testid="bra-receipt">
              <p className="text-sm font-medium text-[#10B981]">Payment received — request submitted</p>
              <p className="text-xs text-white/40 mt-1">
                Your request <span className="font-mono text-white/60">{braReceipt.request_id}</span> is in the admin queue.
                You'll be contacted at <strong className="text-white/60">{user && user.email}</strong> with delivery details.
              </p>
            </div>
          )}

          {braOpen && !braReceipt && (
            <div className="space-y-3 pt-2 border-t border-white/[0.06]" data-testid="bra-form">
              <div>
                <label className="text-xs text-white/40 block mb-2">Delivery method</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'email', label: 'Email', sub: 'Digital PDF', price: 35 },
                    { id: 'mail', label: 'Mail', sub: 'USPS First-Class', price: 40 },
                    { id: 'certified_mail', label: 'Certified Mail', sub: 'Tracked + signature', price: 50 },
                  ].map(function (opt) {
                    var active = braDelivery === opt.id;
                    return (
                      <button key={opt.id} type="button"
                        onClick={function () { setBraDelivery(opt.id); }}
                        className={'p-3 rounded-lg border text-left transition-colors ' + (active ? 'bg-[#5A4E9E]/15 border-[#5A4E9E] text-white' : 'bg-white/[0.02] border-white/10 text-white/60 hover:border-[#5A4E9E]/40')}
                        data-testid={'bra-delivery-' + opt.id}>
                        <p className="text-sm font-semibold">{opt.label} <span className="float-right text-[#5A4E9E]">${opt.price}</span></p>
                        <p className="text-xs text-white/40 mt-0.5">{opt.sub}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/40 block mb-1">First name</label>
                  <input value={braFirstName} onChange={function (e) { setBraFirstName(e.target.value); }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#5A4E9E]"
                    data-testid="bra-first-name" />
                </div>
                <div>
                  <label className="text-xs text-white/40 block mb-1">Last name</label>
                  <input value={braLastName} onChange={function (e) { setBraLastName(e.target.value); }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#5A4E9E]"
                    data-testid="bra-last-name" />
                </div>
                <div>
                  <label className="text-xs text-white/40 block mb-1">Account email</label>
                  <input readOnly value={user && user.email ? user.email : ''}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white/50 cursor-not-allowed"
                    data-testid="bra-email" />
                </div>
                <div>
                  <label className="text-xs text-white/40 block mb-1">Date of request</label>
                  <input readOnly value={todayStr}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white/50 cursor-not-allowed"
                    data-testid="bra-date" />
                </div>
              </div>

              {/* Mailing address — only when delivery is mail / certified mail */}
              {requiresMailing && (
                <div data-testid="bra-mailing-section">
                  <label className="text-xs text-white/40 block mb-1">Mailing address <span className="text-[#EF4444]">*</span></label>
                  <textarea value={braMailingAddress} onChange={function (e) { setBraMailingAddress(e.target.value); }}
                    rows={3}
                    placeholder="Full street address, city, state, ZIP code"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#5A4E9E] resize-none"
                    data-testid="bra-mailing-address" />
                </div>
              )}

              {/* Records to certify */}
              <div data-testid="bra-records-section">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-white/40">Records to certify <span className="text-[#EF4444]">*</span></label>
                  <span className="text-[10px] text-white/35">{Object.keys(braSelectedRefs).length} selected</span>
                </div>
                {braLoadingRecords ? (
                  <p className="text-xs text-white/40">Loading your records…</p>
                ) : braEligible.length === 0 ? (
                  <p className="text-xs text-white/40">You don't have any certifiable records yet. Generate a parenting plan PDF or complete a recorded video call first.</p>
                ) : (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {braEligible.map(function (rec) {
                      var checked = !!braSelectedRefs[rec.reference_number];
                      return (
                        <label key={rec.reference_number} className={'flex items-start gap-2.5 p-2 rounded-md cursor-pointer transition-colors ' + (checked ? 'bg-[#5A4E9E]/15 border border-[#5A4E9E]/40' : 'bg-white/[0.02] border border-white/[0.06] hover:border-[#5A4E9E]/30')}
                          data-testid={'bra-record-' + rec.reference_number}>
                          <input type="checkbox" checked={checked} onChange={function () { toggleBraRecord(rec.reference_number); }}
                            className="mt-0.5 accent-[#5A4E9E]" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#5A4E9E]/20 text-[#5A4E9E] font-bold">{rec.reference_number}</span>
                              <span className="text-[10px] text-white/40 uppercase tracking-wider">{rec.kind_label}</span>
                            </div>
                            <p className="text-sm text-white/80 mt-0.5 truncate">{rec.label}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                <p className="text-sm text-white/50">Total: <strong className="text-white">${BRA_PRICES[braDelivery]}.00</strong></p>
                <button onClick={submitBra} disabled={braSubmitting}
                  className="px-5 py-2.5 bg-[#5A4E9E] text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-30 transition-opacity"
                  data-testid="bra-submit-btn">
                  {braSubmitting ? 'Redirecting to payment…' : 'Pay & Submit Request'}
                </button>
              </div>
              <p className="text-[11px] text-white/30 leading-relaxed">
                You'll be redirected to Stripe to complete payment. Once paid, your request appears in our admin queue for review and delivery.
              </p>
            </div>
          )}
        </div>

        {/* Delete Account */}
        <div className="p-4 bg-[#EF4444]/5 border border-[#EF4444]/15 rounded-xl space-y-3">
          {deletionPending ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#F59E0B]">Account Deletion Scheduled</p>
                  <p className="text-xs text-white/40 mt-1">
                    Your account and all data will be permanently deleted on{' '}
                    <strong className="text-white/60">{deletionDate ? new Date(deletionDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '30 days from request'}</strong>.
                    No shadow data will be retained after deletion.
                  </p>
                </div>
              </div>
              <button onClick={handleCancelDeletion}
                className="w-full py-2.5 bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981] text-sm font-semibold rounded-lg hover:bg-[#10B981]/25 transition-colors"
                data-testid="cancel-deletion-btn">
                Cancel Deletion — Keep My Account
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-[#EF4444]">Delete Account</p>
                  <p className="text-xs text-white/35 mt-0.5">Schedule account deletion with 30-day grace period. No data retained after deletion.</p>
                </div>
                <button onClick={function () { setShowDelete(!showDelete); }}
                  className="px-4 py-2 border border-[#EF4444]/30 text-[#EF4444] text-sm font-medium rounded-lg hover:bg-[#EF4444]/10 flex items-center justify-center gap-2 transition-colors flex-shrink-0"
                  data-testid="show-delete-btn">
                  <XCircle className="w-4 h-4" />
                  {showDelete ? 'Cancel' : 'Delete Account'}
                </button>
              </div>
              {showDelete && (
                <div className="space-y-3 pt-2 border-t border-[#EF4444]/10">
                  <div className="p-3 bg-[#EF4444]/10 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444] flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-white/50">
                        <p className="font-medium text-[#EF4444] mb-1">After 30-day grace period, the following will be permanently deleted:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-white/40">
                          <li>Your profile and personal information</li>
                          <li>Journal entries, calendar events, case files</li>
                          <li>Plan analyses and compliance data</li>
                          <li>Companion profile and chat history</li>
                          <li>Co-parent links and messages</li>
                        </ul>
                        <p className="mt-2 text-white/30">You can cancel deletion anytime during the 30-day period. No shadow storage after deletion.</p>
                      </div>
                    </div>
                  </div>
                  <input type="password" value={deletePassword} onChange={function (e) { setDeletePassword(e.target.value); }}
                    placeholder="Enter your password to confirm"
                    className="w-full bg-white/5 border border-[#EF4444]/20 text-white rounded-lg px-4 py-2 text-sm focus:outline-none"
                    data-testid="delete-password-input" />
                  <button onClick={handleDeleteAccount} disabled={deleting || !deletePassword}
                    className="w-full py-2.5 bg-[#EF4444] text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-30 transition-opacity"
                    data-testid="confirm-delete-btn">
                    {deleting ? 'Scheduling...' : 'Schedule Account Deletion (30-day grace)'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="p-3 bg-white/[0.02] rounded-lg">
        <p className="text-xs text-white/20 text-center">
          For privacy questions, see our Privacy Rights Center. Deletion may be limited where retention is required for legal compliance, billing, security, fraud prevention, or valid legal holds.
        </p>
      </div>
    </div>
  );
}

export default Profile;
